import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Trophy, Users, Calendar, Image as ImageIcon, LogOut, Home as HomeIcon, UserCircle, Database, RefreshCw, KeyRound } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import ManageTeams from '@/components/admin/ManageTeams';
import ManagePlayers from '@/components/admin/ManagePlayers';
import ManageMatches from '@/components/admin/ManageMatches';
import ManagePhotos from '@/components/admin/ManagePhotos';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function AdminDashboard() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('teams');
  const [seeding, setSeeding] = useState(false);

  const handleLogout = async () => {
    await logout();
    navigate('/');
  };

  const handleSeed = async () => {
    setSeeding(true);
    try {
      const { data } = await axios.post(`${API}/admin/seed-tournament`, {}, { withCredentials: true });
      if (data.seeded) {
        toast.success(`Loaded! ${data.teams} teams, ${data.players} players, ${data.matches} matches. Refresh the page.`);
        setTimeout(() => window.location.reload(), 2000);
      } else {
        toast.info(`Database already has data: ${data.teams} teams, ${data.players} players, ${data.matches} matches.`);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to load tournament data');
    } finally {
      setSeeding(false);
    }
  };

  const handleReset = async () => {
    if (!window.confirm('This will DELETE all teams, players, and matches and reload fresh data. Continue?')) return;
    if (!window.confirm('Are you ABSOLUTELY sure? Any live scores or changes will be lost.')) return;
    setSeeding(true);
    try {
      const { data } = await axios.post(`${API}/admin/reset-tournament`, {}, { withCredentials: true });
      toast.success(`Reset complete! ${data.teams} teams, ${data.players} players, ${data.matches} matches.`);
      setTimeout(() => window.location.reload(), 2000);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to reset tournament data');
    } finally {
      setSeeding(false);
    }
  };

  // Change password state
  const [pwOpen, setPwOpen] = useState(false);
  const [currentPw, setCurrentPw] = useState('');
  const [newPw, setNewPw] = useState('');
  const [confirmPw, setConfirmPw] = useState('');
  const [pwSaving, setPwSaving] = useState(false);

  const handleChangePassword = async (e) => {
    e.preventDefault();
    if (newPw !== confirmPw) {
      toast.error('New passwords do not match');
      return;
    }
    if (newPw.length < 8) {
      toast.error('Password must be at least 8 characters');
      return;
    }
    setPwSaving(true);
    try {
      await axios.post(
        `${API}/auth/change-password`,
        { current_password: currentPw, new_password: newPw },
        { withCredentials: true }
      );
      toast.success('Password updated successfully');
      setPwOpen(false);
      setCurrentPw('');
      setNewPw('');
      setConfirmPw('');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to change password');
    } finally {
      setPwSaving(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#0A0F1C] hexagram-pattern">
      <header className="sticky top-0 z-50 glass-card border-b border-white/10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-20">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 rounded-full bg-gradient-to-br from-[#D4AF37] to-[#0038B8] flex items-center justify-center">
                <Trophy className="w-6 h-6 text-white" />
              </div>
              <div>
                <h1 className="text-2xl font-bold text-white tracking-tight">Admin Dashboard</h1>
                <p className="text-[#9BA3AF] text-sm flex items-center gap-2">
                  <UserCircle className="w-4 h-4" />
                  {user?.name} ({user?.email})
                </p>
              </div>
            </div>
            <div className="flex items-center gap-3 flex-wrap">
              <Button
                data-testid="seed-data-button"
                onClick={handleSeed}
                disabled={seeding}
                variant="outline"
                className="border-[#D4AF37]/40 text-[#D4AF37] hover:bg-[#D4AF37]/10"
              >
                <Database className="w-4 h-4 mr-2" />
                {seeding ? 'Loading...' : 'Load Data'}
              </Button>
              <Button
                data-testid="reset-data-button"
                onClick={handleReset}
                disabled={seeding}
                variant="outline"
                className="border-red-500/40 text-red-400 hover:bg-red-500/10"
              >
                <RefreshCw className="w-4 h-4 mr-2" />
                Reset
              </Button>
              <Dialog open={pwOpen} onOpenChange={setPwOpen}>
                <DialogTrigger asChild>
                  <Button
                    data-testid="change-password-button"
                    variant="outline"
                    className="border-[#0038B8]/40 text-[#7AA0FF] hover:bg-[#0038B8]/10"
                  >
                    <KeyRound className="w-4 h-4 mr-2" />
                    Change Password
                  </Button>
                </DialogTrigger>
                <DialogContent className="bg-[#131C2F] border-white/10 text-white max-w-md">
                  <DialogHeader>
                    <DialogTitle className="flex items-center gap-2">
                      <KeyRound className="w-5 h-5 text-[#D4AF37]" />
                      Change Your Password
                    </DialogTitle>
                  </DialogHeader>
                  <form onSubmit={handleChangePassword} className="space-y-4 pt-2">
                    <div>
                      <Label className="text-white mb-2">Current Password</Label>
                      <Input
                        data-testid="current-password-input"
                        type="password"
                        value={currentPw}
                        onChange={(e) => setCurrentPw(e.target.value)}
                        required
                        className="bg-[#0A0F1C] border-white/20 text-white"
                      />
                    </div>
                    <div>
                      <Label className="text-white mb-2">New Password</Label>
                      <Input
                        data-testid="new-password-input"
                        type="password"
                        value={newPw}
                        onChange={(e) => setNewPw(e.target.value)}
                        required
                        minLength={8}
                        className="bg-[#0A0F1C] border-white/20 text-white"
                      />
                      <p className="text-[#9BA3AF] text-xs mt-1">At least 8 characters. Use a mix of letters, numbers and symbols.</p>
                    </div>
                    <div>
                      <Label className="text-white mb-2">Confirm New Password</Label>
                      <Input
                        data-testid="confirm-password-input"
                        type="password"
                        value={confirmPw}
                        onChange={(e) => setConfirmPw(e.target.value)}
                        required
                        className="bg-[#0A0F1C] border-white/20 text-white"
                      />
                    </div>
                    <Button
                      type="submit"
                      data-testid="save-password-button"
                      disabled={pwSaving}
                      className="w-full bg-[#D4AF37] hover:bg-[#F3E5AB] text-[#0A0F1C] font-bold"
                    >
                      {pwSaving ? 'Saving...' : 'Update Password'}
                    </Button>
                  </form>
                </DialogContent>
              </Dialog>
              <Link to="/">
                <Button
                  data-testid="view-public-site-button"
                  variant="outline"
                  className="border-white/20 text-white hover:bg-white/10"
                >
                  <HomeIcon className="w-4 h-4 mr-2" />
                  View Site
                </Button>
              </Link>
              <Button
                data-testid="logout-button"
                onClick={handleLogout}
                className="bg-red-500/20 text-red-400 hover:bg-red-500/30 border border-red-500/30"
              >
                <LogOut className="w-4 h-4 mr-2" />
                Logout
              </Button>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
          <TabsList className="bg-[#131C2F] border border-white/10 p-1">
            <TabsTrigger
              value="teams"
              data-testid="admin-tab-teams"
              className="data-[state=active]:bg-[#D4AF37] data-[state=active]:text-[#0A0F1C]"
            >
              <Trophy className="w-4 h-4 mr-2" />
              Teams
            </TabsTrigger>
            <TabsTrigger
              value="players"
              data-testid="admin-tab-players"
              className="data-[state=active]:bg-[#D4AF37] data-[state=active]:text-[#0A0F1C]"
            >
              <Users className="w-4 h-4 mr-2" />
              Players
            </TabsTrigger>
            <TabsTrigger
              value="matches"
              data-testid="admin-tab-matches"
              className="data-[state=active]:bg-[#D4AF37] data-[state=active]:text-[#0A0F1C]"
            >
              <Calendar className="w-4 h-4 mr-2" />
              Matches
            </TabsTrigger>
            <TabsTrigger
              value="photos"
              data-testid="admin-tab-photos"
              className="data-[state=active]:bg-[#D4AF37] data-[state=active]:text-[#0A0F1C]"
            >
              <ImageIcon className="w-4 h-4 mr-2" />
              Photos
            </TabsTrigger>
          </TabsList>

          <TabsContent value="teams">
            <ManageTeams />
          </TabsContent>
          <TabsContent value="players">
            <ManagePlayers />
          </TabsContent>
          <TabsContent value="matches">
            <ManageMatches />
          </TabsContent>
          <TabsContent value="photos">
            <ManagePhotos />
          </TabsContent>
        </Tabs>
      </main>
    </div>
  );
}
